import type { ProductV2 } from "@autumn/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useQueryKeyFactory } from "@/hooks/common/useQueryKeyFactory";
import { useAxiosInstance } from "@/services/useAxiosInstance";

const EMPTY_PRODUCTS: ProductV2[] = [];

/**
 * Fetch plans that are linked as licenses somewhere in the catalog. The main
 * `/products/products` list still includes them; list UIs filter them out.
 */
export const useLicenseProductsQuery = ({
	enabled = true,
	allVersions = false,
}: {
	enabled?: boolean;
	allVersions?: boolean;
} = {}) => {
	const axiosInstance = useAxiosInstance();
	const queryClient = useQueryClient();
	const buildKey = useQueryKeyFactory();

	const { data, isLoading, error, refetch } = useQuery<{
		products: ProductV2[];
	}>({
		queryKey: buildKey(["license_products", allVersions ? "all" : "latest"]),
		queryFn: async () => {
			const { data } = await axiosInstance.get("/products/license_products", {
				params: allVersions ? { all_versions: true } : undefined,
			});
			return data;
		},
		enabled,
	});

	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: ["license_products"] });

	return {
		licenseProducts: data?.products ?? EMPTY_PRODUCTS,
		isLoading,
		error,
		refetch,
		invalidate,
	};
};
